/**
 * UI-8f Task 7 — IncidentTimeline.
 *
 * Renders the chronological note feed for an incident on
 * IncidentDetailPage. The backend appends notes in order to the
 * `incidents.notes` jsonb array, so we render the array as-is — no
 * client-side sorting needed.
 *
 * Each entry: `formatDateTime(n.at)` — `<code>{n.by.slice(0,8)}</code>`
 * — `{n.text}`. The short `by` slice keeps the user-id chip compact;
 * a future UI-9 deliverable can render the user's full name once we
 * have a /users/:id lookup endpoint.
 *
 * Below the list, the append-note UI (textarea + "Add note" button) is
 * shown ONLY when `incident.status !== 'CLOSED'`. Closed incidents are
 * read-only — matches the backend permission gate that lets you append
 * a note any time except after CLOSED.
 *
 * The button is disabled until the textarea has non-whitespace text.
 * After a successful append the textarea clears so the operator can
 * keep adding notes without re-focusing.
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputTextarea } from 'primereact/inputtextarea'

import { formatDateTime } from '../../utils/formatDate.js'
import type { Incident, Note } from '../../hooks/useIncidents.js'

export interface IncidentTimelineProps {
  incident: Incident
  onAddNote: (text: string) => Promise<void>
}

export function IncidentTimeline({
  incident,
  onAddNote,
}: IncidentTimelineProps): JSX.Element {
  const [newNote, setNewNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const trimmed = newNote.trim()
  const canSubmit = trimmed.length > 0 && !submitting
  const showAppendUi = incident.status !== 'CLOSED'

  async function handleAdd(): Promise<void> {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await onAddNote(trimmed)
      setNewNote('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card title="Timeline">
      {incident.notes.length === 0 ? (
        <p className="text-sm text-gray-500">No notes yet.</p>
      ) : (
        <ol className="space-y-2" data-testid="incident-notes">
          {incident.notes.map((n: Note, i) => (
            <li key={`${n.at}-${i}`} className="text-sm">
              <time
                dateTime={n.at}
                className="text-gray-500"
              >
                {formatDateTime(n.at)}
              </time>
              {' — '}
              <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                {n.by.slice(0, 8)}
              </code>
              {' — '}
              <span>{n.text}</span>
            </li>
          ))}
        </ol>
      )}

      {showAppendUi && (
        <div className="mt-4 flex flex-col gap-2" data-testid="add-note-form">
          <label
            htmlFor="incident-new-note"
            className="text-sm font-medium"
          >
            Add a note
          </label>
          <InputTextarea
            id="incident-new-note"
            value={newNote}
            onChange={(e) => {
              setNewNote(e.target.value)
            }}
            rows={3}
            maxLength={2000}
            placeholder="What's the latest?"
          />
          <div className="flex justify-end">
            <Button
              label="Add note"
              disabled={!canSubmit}
              loading={submitting}
              onClick={() => {
                void handleAdd()
              }}
            />
          </div>
        </div>
      )}
    </Card>
  )
}

export default IncidentTimeline
